import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AppInsightsService } from '@cudirect/shared-ui-library';
import { DynamicGuardService } from '../dynamic-guard/dynamic-guard.decorator';
import { FlowInfo, getFlowInfo } from '../helper/flow-utility/flow-utility';
import { StorageService } from '../service/storage/storage.service';
import { NavigationPageState } from '../service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { WorkflowPageNavigationService } from '../service/workflow-page-navigation/workflow-page-navigation.service';
import { CustomUserInfo, InvalidPage } from '@workflow/class/custom-userinfo.type';
import { StatefulService } from '@app/service/stateful.service';
import { PageInstance } from '@workflow/class/page.service';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';

const OAO = 'oao';

/** An activate guard to prevent navigating to a page if all required pages are not completed  */
@DynamicGuardService('RequiredPagesCompleteAsyncGuard')
@Injectable({ providedIn: 'root' })
export class RequiredPagesCompleteAsyncGuard {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly router: Router,
    private readonly workflowPageNavigationService: WorkflowPageNavigationService,
    private readonly storageService: StorageService,
    protected readonly applicationInsightsService: AppInsightsService,
    private readonly flowService: FlowService
  ) {}
  /**
   * Activate page if the page is enabled, other wise it will redirect to the first incomplete page in the current flow
   */
  public canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return new Promise<boolean>((resolve) => {
      this.storageService.backendInit$.subscribe((init) => {
        if (init) {
          this.areRequiredPagesComplete(next, state, resolve);
        }
      });
    });
  }

  /**
   * This method checks if all required pages in the workflow are complete before navigating to the next page.
   *
   * @param next - The snapshot of the next route to be activated.
   * @param state - The current state of the router, including the URL.
   * @param resolve - A callback function to resolve the check with a boolean value.
   *
   * @remarks
   * This method is exclusively called within the OAO flow. It verifies if the required pages
   * in the current workflow are complete. If the pages are not complete, it navigates to the first incomplete
   * page and logs the loopback event. If the pages are complete, it allows the navigation to proceed.
   * TODO: We should make sure that we have a version of this for ola, and that we are also capturing OLA loopback state similarly
   *
   * @throws Will throw an error if no workflow or page is found for the given URL.
   */
  private areRequiredPagesComplete(next: ActivatedRouteSnapshot, state: RouterStateSnapshot, resolve: (r: boolean) => void): void {
    // info of the next page that is going to be visited
    const nextPath = this.cleanPath(state.url);
    const pageUrlHelper = this.flowService.createPageUrlHelper(next);
    const flow = pageUrlHelper.getFlow(nextPath);
    const page = pageUrlHelper.getPageFlow(nextPath);

    if (!flow || !page) {
      throw Error(`No work flow or page has been found for the URL ${state.url}`);
    }

    if (!page.visible) {
      // TODO: revisit / refactor
      // Fix this hardcoded value
      this.router.navigate(['/consumer/dashboard']);
      resolve(false);
      return;
    }

    const flowInfo = getFlowInfo(flow);

    const objFirstInvalidPage = this.getOAOFirstInvalidPage(flow);

    // Check all the required page of the next page are complete
    if (page.helper.allRequiredPagesComplete(flowInfo.pages)) {
      if (objFirstInvalidPage) {
        setTimeout(() => {
          this.workflowPageNavigationService.navigateToPage(objFirstInvalidPage);
        }, 200);
      }
      resolve(true);
      return;
    }

    const incompletePages = this.getIncompletePages(flowInfo, page);
    if (incompletePages.length > 0) {
      const userInfo = this.buildCustomUserInfoData(incompletePages);
      this.applicationInsightsService.logEvent('ecp.ui.oaoLoopBack', {
        info: userInfo,
        message: 'User looped back in the application flow'
      });
      setTimeout(() => {
        this.workflowPageNavigationService.navigateToPage(incompletePages[0]);
      }, 200);
      resolve(true);
      return;
    }

    resolve(false);
    return;
  }

  private cleanPath(url: string): string {
    const path = url.startsWith('/') || url.startsWith('\\') ? url.substring(1) : url;
    return path.split('?')[0].split('#')[0];
  }

  private getIncompletePages(flowInfo: FlowInfo, page: PageInstance): PageInstance[] | null {
    const requiredPages = page.helper.getRequiredPages(flowInfo.pages);
    if (requiredPages.length === 0) {
      return null;
    }

    const incompletePages = requiredPages.filter((r) => !r.helper.isPageComplete);
    return incompletePages;
  }

  public getOAOFirstInvalidPage(flow: FlowInstance): PageInstance | null {
    let objFirstInvalidPage: PageInstance = null;
    let hasCompletedPageIssues = false;
    if (flow.id.toLowerCase().indexOf(OAO) > -1 && this.statefulService.wrap('api:extraData').valueStringOnly) {
      const objPageStateJSON = JSON.parse(this.statefulService.wrap('api:extraData').valueStringOnly) as NavigationPageState;
      const completedPages = flow.pages.filter((t) => t.visible && t.helper.isPageComplete);
      if (completedPages && completedPages.length > 0) {
        completedPages.forEach((page: PageInstance) => {
          if (!hasCompletedPageIssues && objPageStateJSON[page.id]?.isVisited && objPageStateJSON[page.id].state.indexOf('issues') >= 0) {
            objFirstInvalidPage = page;
            hasCompletedPageIssues = true;
            console.error('Loopback - First Invalid Page:', objFirstInvalidPage);
          }
        });
      }
    }
    return objFirstInvalidPage;
  }

  /**
   * Method to build/construct User Info data needed for appinsights
   * @param pages - array of incomplete pages
   * @returns userInfo of type:CustomUserInfo
   */
  public buildCustomUserInfoData(pages: PageInstance[] = []): CustomUserInfo {
    if (!pages?.length) {
      return;
    }

    const userFirstName = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').valueString;
    const userLastName = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/lastName').valueString;
    const userEmail = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString;
    const applicationId = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    const primaryApplicantApplicantId = this.statefulService.wrap(
      'api:oaoMemberInformation/accountsAndServices/primaryApplicant/applicantId'
    ).valueString;
    const invalidPages: InvalidPage[] = [];

    if (pages.length > 0) {
      pages.forEach((item) => {
        const invalidFields = item.helper.invalidFields;
        const arrayFieldLabels: string[] = [];
        invalidFields.forEach((field) => {
          arrayFieldLabels.push(field.config.label);
        });
        invalidPages.push({ pageId: item.id, title: item.config.title, invalidFieldLabels: arrayFieldLabels });
      });
    }

    const userInfo = {
      applicantId: primaryApplicantApplicantId,
      applicationId,
      email: userEmail,
      lastName: userLastName,
      firstName: userFirstName,
      invalidPages
    } as CustomUserInfo;

    return userInfo;
  }
}
